import PlumXLogo from './PlumXLogo';
import { buildDetailsLink } from '@plumx/utils';
import { groupByLabel, toDetailsLinkOptions } from '../utils/stats';
import { CATEGORIES, visibleCategories } from '../utils/categories';
import { useTranslation } from '../i18n/i18n';
import { isAlternateTheme } from '../utils/theme';
import type {
  WidgetData,
  WidgetMetrics,
  WidgetConfig,
  IdentifierType,
  WidgetTheme,
} from '../types';
import styles from '../styles/widget.module.scss';

interface MetricsPanelProps {
  metrics: WidgetMetrics;
  data: WidgetData;
  config: WidgetConfig;
  identifier: string;
  identifierType: IdentifierType;
  theme?: WidgetTheme;
}

const MetricsPanel = ({
  metrics,
  data,
  config,
  identifier,
  identifierType,
  theme,
}: MetricsPanelProps) => {
  const { t } = useTranslation();
  const detailsLink = buildDetailsLink(identifier, identifierType, toDetailsLinkOptions(config));
  const hideHeader = isAlternateTheme(theme);

  return (
    <div className={styles.metricsPanel}>
      {!hideHeader && (
        <div className={styles.metricsPanelHeader}>
          <PlumXLogo variant="white" size="md" />
        </div>
      )}
      <div className={styles.metricsPanelBody}>
        {visibleCategories(CATEGORIES, metrics, config).map(
          ({ label, metricsKey, statsKey, aggregation }) => {
            const grouped = groupByLabel(data.statistics[statsKey] ?? [], aggregation);
            return (
              <div
                key={metricsKey}
                className={`${styles.panelCategory}${hideHeader ? ` ${styles.panelCategoryWithBar}` : ''}`}
                data-metric={metricsKey}
              >
                {hideHeader ? (
                  <div className={styles.panelCategoryHeader}>
                    <span className={styles.panelCategoryLabel}>{t(label)}</span>
                  </div>
                ) : (
                  <div
                    className={`${styles.panelCategoryLabel} ${styles.panelCategoryLabelAccent}`}
                  >
                    {t(label)}
                  </div>
                )}
                {grouped.map(({ label: statLabel, total }) => (
                  <div
                    key={statLabel}
                    className={`${styles.panelStatRow}${hideHeader ? ` ${styles.panelStatRowSpaced}` : ''}`}
                  >
                    {hideHeader ? (
                      <>
                        <span>{t(statLabel)}</span>
                        <strong>{total.toLocaleString()}</strong>
                      </>
                    ) : (
                      <>
                        {t(statLabel)}: <strong>{total.toLocaleString()}</strong>
                      </>
                    )}
                  </div>
                ))}
              </div>
            );
          },
        )}
      </div>
      {!config.disableLinkToPlumx && (
        <div
          className={`${styles.metricsPanelFooter}${hideHeader ? ` ${styles.metricsPanelFooterWithLogo}` : ''}`}
        >
          {hideHeader && <PlumXLogo variant="color" size="sm" />}
          <a
            href={detailsLink}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.seeDetails}
          >
            {t('see details')}
            <svg
              className={styles.linkIcon}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 128 128"
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                fillRule="evenodd"
                d="M18 109.855V18.14h36v10H28v71.715h72V74.14h9.996v35.715zM69.98 28.14v-10h40v40h-10V35.21l-35.8 35.82-7.08-7.08 35.81-35.81z"
                clipRule="evenodd"
              />
            </svg>
          </a>
        </div>
      )}
    </div>
  );
};

export default MetricsPanel;
